import { useDropzone, Accept } from 'react-dropzone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import { usePyrenzAlert } from '~/provider';

interface DropzoneProps {
  onDrop: (acceptedFiles: File[]) => void;
  label?: string;
  className?: string;
  initialImage?: string | null;
}

export function Dropzone({
  onDrop,
  label = 'Drag & drop an image or click to upload',
  className,
  initialImage,
}: DropzoneProps) {
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(
    initialImage || null
  );
  const showAlert = usePyrenzAlert();

  useEffect(() => {
    if (initialImage) {
      setBannerImagePreview(initialImage);
    }
  }, [initialImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          showAlert('Please upload an image file.', 'Alert');
          return;
        }
        if (file.size > 1024 * 1024) {
          showAlert(
            'File size exceeds 1 MB. Please choose a smaller file.',
            'Alert'
          );
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result as string;
          setBannerImagePreview(base64data);
        };
        reader.readAsDataURL(file);
        onDrop(acceptedFiles);
      }
    },
    accept: {
      'image/*': ['.jpeg', '.png', '.gif'],
    } as Accept,
    multiple: false,
  });

  return (
    <Box
      {...getRootProps()}
      className={clsx('w-full cursor-pointer', className)}
      sx={{
        border: '2px dashed grey',
        borderRadius: '16px',
        p: 1,
        bgcolor: isDragActive ? 'rgba(0, 0, 0, 0.7)' : 'transparent',
        transition: 'transform 0.3s ease, background-color 0.3s ease',
        '&:hover': {
          bgcolor: 'rgba(0, 0, 0, 0.1)',
          transform: 'scale(1.02)',
        },
      }}
    >
      <input {...getInputProps()} />
      {bannerImagePreview ? (
        <Box
          component="img"
          src={bannerImagePreview}
          alt={label}
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: '12px',
            display: 'block',
            objectFit: 'cover',
          }}
        />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            minHeight: '180px',
            color: '#ccc',
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: '3rem' }} />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            {label}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
